/** @typedef {import('@common/i18n/definitions').TLanguage} TLanguage */
/** @typedef {import('./definitions').ISoundImprint} ISoundImprint */

/** @typedef {'tree' | 'game' | 'userGuide'} TNarration */
/** @typedef {'none'} TNoNarration */
/** @typedef {'v2' | 'v3' | 'v4' | 'edu' | 'earl' | 'presentation' | 'idoctum' | 'bible' | 'simple'} TEngine */
/** @typedef {'math' | 'logopedy' | 'grammar' | 'foreign-language' | 'literature' | 'environmental-biology' | 'biology' | 'geography' | 'health-promotion-and-mental-health' | 'chemistry' | 'history' | 'physics' | 'music' | 'traffic' | 'chess'} TSubject */
/** @typedef {'done' | 'development' | 'planned' | 'paused'} TDevPhase */
/** @typedef {'#de3831' | '#f49123' | '#f15d3f' | '#4db4d2' | '#e5bd78' | '#87c543' | '#518f40' | '#945e3b' | '#f49123' | '#87579e' | '#07867f' | '#118acb' | '#9f1c36' | '#f49123' | '#f49123'} TSubjectColor */
/** @typedef {'full' | 'demo' | 'check'} TDemoStatus */
/** @typedef { 'pixabay' | 'bensound' } TSoundOrigin */

const MAX_PRELOADER_IMAGE_SIZE  = 30000

/** @type {readonly number[]} */
const TOBII_APPS = [ 141, 142, 143, 144, 145 ]

/** @type {readonly number[]} */
const EARL_APPS = [ 180, 181, 182, 183, 184, 185, 186, 187, 188, 189, 190, 191, 192, 193, 194, 195, 196 ]

/** @type {readonly number[]} */
const BIBLE_APPS = [ 290, 291, 292, 293, 294, 295, 296, 297, 298, 299 ]

/** @type {readonly number[]} */
const ENGLISH_ONLY_APPS = [ 200, 201, 202, 203, 204, 205, 206, 207, 208, 209 ]

/** @type {readonly number[]} */
const HUNGARIAN_ONLY_APPS = [ 212, 213, 215, 216, 217, 218, 219, 220, 222, 223 ]

/** @type {readonly number[]} */
const GERMAN_ONLY_APPS = [ 260, 261, 262, 263, 264, 265, 266, 267, 268, 269 ]

/** @type {readonly number[]} */
const DRONE_APPS = [ 240, 241, 242, 243, 244, 245, 246, 247, 248, 249 ]

/** @type {readonly number[]} */
const IDOCTUM_APPS = [ 300 ]

/** @type {readonly number[]} */
const EDU_APPS = [ 166, 169, 170, 171, 172 ]

/** @type {readonly number[]} */
const SIMPLE_APPS = [ 320 ]

/** @type {readonly number[]} */
const KATA_APPS = [ 127, 120, 139, 102, 128, 137, 133, 135, 118, 113 ]

/**
 * Kozinak itt első helyen kell lennie.
 * @type {readonly number[]}
 */
const KOZI_APPS = [
  130, 141, 144, 151, 149, 117, 121, 147, 114, 142, 125, 126, 138, 140, 143, 156, 273, 274
]

/** @type {readonly number[]} */
const IGOR_APPS = [ 104, 118, 145, 109, 119, 106, 121, 160, 102, 401, 404, 407, 408, 413 ]

/** @type {readonly number[]} */
const SZABI_APPS = [ 104, 103, 152 ]

/** @type {readonly number[]} */
const BRIGI_APPS = [
  159, 160, 163, 173, 175, 179,
  // Angol appok
  201, 205,
  // Magyar nyelvtan appok
  211, 215,
  // Betűzőgép
  214,
  // Drón appok
  240, 241, 243, 249,
  // Német appok
  264,
  // Csodaszoba
  270,
  // Számláló-2
  275,
  // Robit-kaland
  280,
  // Bible
  290, 292, 293, 294, 296, 297, 298,
  // idoctum
  300
]

/** @type {readonly number[]} */
const ESZTER_APPS = [
  162, 167, 176, 178,
  // Angol appok
  200, 202, 203, 204, 206, 208, 212, 213,
  // Magyar appok
  217, 218, 220, 222, 223, 272,
  // Drón appok
  242, 244,
  // Térképes appok
  250, 251, 252,
  // Német appok
  260, 262, 263, 265, 267, 268,
  281,
  // Bible appok
  291, 295, 299
]

/** @type {readonly number[]} */
const BARBI_APPS = [
  159, 160, 167, 173, 175, 176, 177, 178,
  179,
  ...EDU_APPS,
  ...EARL_APPS,
  ...SIMPLE_APPS,
  ...ENGLISH_ONLY_APPS,
  211,
  ...HUNGARIAN_ONLY_APPS,
  // Betűzőgép
  214,
  221,
  ...DRONE_APPS,
  // Térképes appok
  250, 251, 252,
  // Csodaszoba
  270,
  // Szorzódzsungel és Mondatlajhár
  271, 272,
  // Számláló-2
  275,
  // Robit-kaland
  280, 281,
  // idoctum
  300,
  // Egyéb
  320,
  401, 402, 403, 405, 406
]

/** @type {readonly number[]} */
const NORI_APPS = [ 400, 404, 407, 408, 409, 410, 413 ]

/** @type {readonly number[]} */
const RAMY_APPS = [
  ...EARL_APPS
]

/** @type {readonly number[]} */
const PETER_APPS = [
  ...EARL_APPS // Péter besegített a klónoknál
]

/** @type {readonly number[]} */
const BALAZS_APPS = []

/** @type {readonly number[]} */
const MARTIN_APPS = [ 400, 401, 403, 404, 405, 407, 410, 412 ]

/** @type {readonly number[]} */
const LINDA_APPS = [ 400, 403, 404, 405, 407, 408, 410, 412, 414, 415 ]

/** @type {readonly number[]} */
const LUCA_APPS = [ 401, 402, 404, 409 ]

/** @type {readonly number[]} */
const MOZES_APPS = [ 406, 410, 413 ]

/** @type {readonly number[]} */
const MATE_APPS = [ 410, 412, 414 ]

/** @type { readonly number[] } */
const ADRI_APPS = [ 412, 414 ]

/** @type {readonly number[]} */
const DEBI_APPS = [
  ...KATA_APPS, // Debi felújította az összes Kata appot
  103, 104, // Debi ezekbe is belejavított
  // Ezek meg teljesen a sajátjai
  157, 115, 148, 105, 150, 154, 106, 119, 109, 164, 161, 155,
  145, 146, 153, 168, 177, 271,
  // Debi csinálta az összes edu appot
  ...EDU_APPS,
  // Debi csinálta az összes klónappot
  ...EARL_APPS,
  // Angol appokból
  207, 209,
  // Magyar nyelvtan appok
  216, 219, 221,
  // Megadom
  230,
  // Drón appok
  245, 246, 247, 248,
  // Német appok
  266, 269,
  // Egyéb
  320
]

/** @type {readonly number[]} */
const FRUANYU_APPS = [
  102, 103, 104, 105, 106, 109, 113, 114, 115, 117, 118, 119, 120, 121, 125, 126, 127, 128,
  130, 133, 135, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151,
  152, 153, 154, 155, 157, 160, 161, 162, 163, 164, 168, 273, 274
]

/**
 * Német fordító.
 * @type {readonly number[]}
 */
const KRISZTINA_APPS = [
  // Német appok.
  ...GERMAN_ONLY_APPS
]

const CANVAS_APPS = [
  280,
  411,
  502, // Mert benne van a Robit-kaland modulja.
  504, // Mert benne van a Robit-kaland modulja.
  505  // Mert benne van a Robit-kaland modulja.
]

/** Berci ezeket az appokat narrálja. */
const NARRATION_BERCI_APPS = [ 159 ]

const NARRATION_NORI_APPS = [
  104, 117, 120, 127, 147,
  148, 151, 153, 158, 160,
  161, 164, 168, 175, 273,
  274, 275, 290, 157, 406,
  138, 125
]

/** @type {readonly string[]} */
const APPS_WITH_CUSTOM_USERGUIDE = [
  'picipiac',
  'csomester',
  'erzelmek',
  'etelpiramis',
  'vakondkaland',
  'tisztatisza',
  'kodkapitany',
  'okosvaros-2050',
  'sundorgo',
  'autodron',
  'mezogazdasagi-dron',
  'honvedelmi-dron',
  'tuzolto-dron',
  'lepcsojaro',
  'szorzodzsungel',         // utils-ba van kivezetve a userGuide kezelése
  'muveletcirkusz'          // Folyamatosan látható az akutális utasitás.
]

/** @type {readonly string[]} */
const APPS_WITH_EXTENDED_USERGUIDE = [
  'domino2',
  'kepkockak',              // a BaseCameraGame-ből származik.
  'meroszam',
  'muvelettabla',
  'szamtomb',
  'sasles',                 // HawkShaw-ból származik.
  'present',                // egyedi megoldás
  'future',                 // a present-ből származik.
  'past',                   // a present-ből származik.
  'prateritum-und-perfekt', // present klón
  'prasens',                // present klón
  'szosziget',              // BasePackerGame-ből származik.
  'word-world',             // A BasePairingGame-ből és a BaseCardFlippingGame-ből származik, ott van userGuide
  'welt-wort',              // Word world klón
  'futur',                  // a present-ből származik.
  'hasenfinder',            // a catfinder-ből származik.
  'muster-detektiv',        // HawkShaw-ból származik.
  'rechtschreibung',        // BasePackerGame-ből származik.
  'szamlalo-2',             // BaseGameScene-ből származik.
  'galambgyujto',           // BaseGameScene-ből származik.
  'galambtar',              // BaseGameScene-ből származik.
  'bolygokozi-kaland'       // BaseGame-ből származik.
]

/** Ezeknél a játékoknál png kiterjesztésű pályaválasztó spritesheetet generálunk, a többinél jpg. */
const APPS_WITH_PNG_LEVEL_SPRITESHEET = [ 'kodkapitany', 'word-world', 'welt-wort', 'irregular-verbs', 'emberi-test' ]

/** @type {readonly number[]} */
const ADVANCED_PRELOADABLE_APPS = [
  130 // Betűszafari
]

module.exports = {
  APPS_WITH_PNG_LEVEL_SPRITESHEET,
  MAX_PRELOADER_IMAGE_SIZE,
  TOBII_APPS,
  EDU_APPS,
  EARL_APPS,
  BIBLE_APPS,
  SIMPLE_APPS,
  CANVAS_APPS,
  ENGLISH_ONLY_APPS,
  HUNGARIAN_ONLY_APPS,
  GERMAN_ONLY_APPS,
  DRONE_APPS,
  KATA_APPS,
  KOZI_APPS,
  IGOR_APPS,
  SZABI_APPS,
  BRIGI_APPS,
  NORI_APPS,
  ESZTER_APPS,
  LINDA_APPS,
  LUCA_APPS,
  MOZES_APPS,
  MATE_APPS,
  BARBI_APPS,
  RAMY_APPS,
  PETER_APPS,
  BALAZS_APPS,
  MARTIN_APPS,
  ADRI_APPS,
  DEBI_APPS,
  FRUANYU_APPS,
  KRISZTINA_APPS,
  APPS_WITH_CUSTOM_USERGUIDE,
  APPS_WITH_EXTENDED_USERGUIDE,
  NARRATION_BERCI_APPS,
  NARRATION_NORI_APPS,
  IDOCTUM_APPS,
  ADVANCED_PRELOADABLE_APPS
}

// !!! HA VESZEL FEL ÚJ ZENÉKET, AKKOR NE FELEDD EL
// !!! FELVENNI A HANGEREJÉT ÉS HA SZÜKSÉGES
// !!! A HOZZÁTARTOZÓ IMPRINT RÉSZ IS AZ APPSHELL/CONSTS-BAN.
/** @type {readonly number[]} */
module.exports.MUSIC_TYPES = [
  0,  // game_music_dronpilota                    // .4
  1,  // game_music_szorzodzsungel                // .4
  2,  // main_music_autodron                      // .4
  3,  // main_music_betufalva                     // .2
  4,  // main_music_bolygokozi-kaland             // .2
  5,  // main_music_csomester                     // .3
  6,  // main_music_dronforgalom                  // .4
  7,  // main_music_dronkuldetes                  // .4
  8,  // main_music_dronpilota                    // .4
  9,  // main_music_dronposta                     // .4
  10, // main_music_dronterkep, edus appok        // .4
  11, // main_music_dronzona                      // .4
  12, // main_music_erdei-fogado                  // .4
  13, // main_music_gyongygyar                    // .3
  14, // main_music_hidjavito                     // .2
  15, // main_music_honvedelmi-dron               // .4
  16, // main_music_kodkapitany                   // .15
  17, // main_music_mezogazdasagi-dron            // .4
  18, // main_music_muveletcirkusz                // .2
  19, // main_music_okosvaros-2050                // .4
  20, // main_music_robit-kaland                  // .4
  21, // main_music_szallito                      // .2
  22, // main_music_szambuvesz                    // .2
  23, // main_music_szamsztori                    // .2
  24, // main_music_tortatortek                   // .5
  25, // main_music_tuzolto-dron                  // .2
  26, // main_music_xmas_gyongygyar               // .3
  27, // main_music_triumviratus                  // .3
  28, // new_music_kis-felfedezo                  // .3
  29, // new_music_orszagjaro-(1,2) / europajaro  // .2
  30, // new_music_rejtozkodo                     // .25  Friendly Town – Fun Video Game Music Loop
  31, // new_music earl appok                     // .3   Upbeat Background Loop - Casual Video Game Music
  32, // new_music varazsdallam                   // .3   Game Background – Fun and Uplifting Adventure Music
  33, // new_music betuszafari, szamszafari       // .3   Africa
  34, // new_music                                // .3   Kevin Macleod – Wallpaper
  35, // new_music                                // .4   Komiku – Shopping List
  36, // new_music                                // .3   Bensound – Smile
  37, // new_music sztoridetektiv                 // .4   Pixabay -  (Strange detectives) HarumachiMusic,
  38, // new_music melytengeri-uzenetek           // .3   Pixabay
  39  // new_music melytengeri-uzenetek           // .5   Pixabay - Little Secrets - Fairy Tale
]

/** @type {TReadonlyRecord<TSoundOrigin, ISoundImprint>} */
module.exports.SOUND_IMPRINTS = {
  pixabay:  {
    name: 'Pixabay',
    link: 'https://www.pixabay.com/'
  },
  bensound: {
    name: 'Bensound',
    link: 'https://www.bensound.com/'
  }
}

/**
 * Az összes lehetséges nyelv.
 * @url ISO country code-ot használunk: https://countrycode.org/
 */
module.exports.AVAILABLE_LANGUAGES = [ 'hu', 'en', 'de', 'si', 'rs', 'es', 'pl', 'ua', 'ro', 'sk' ]

/**
 * Melyik engine-el készült az app?
 *
 * V2: Minden, ami a Tiszta-Tisza játék előtti.
 * V3: Letisztultabb, a v2 hülyeségeit javítva vagy elhagyva.
 * V4: Szigorúbb GameScene és Game kezelés absztrakcióval.
 * Edu: Oktatási app, teljesen különbözik a hagyományos Ügyesedni játékoktól.
 * Presentation: Markating célre használt app, még alakulóban.
 * Earl: Az EARL robothoz 17 egységes felépítésű klónapplikáció.
 * Idoctum: Az iDoctumos appok integrálása az Ügyesednibe.
 * @type {TKeyValuePairs<TEngine>}
 */
module.exports.ENGINE = {
  v2: 'v2',
  v3: 'v3',
  v4: 'v4',
  edu: 'edu',
  earl: 'earl',
  bible: 'bible',
  simple: 'simple',
  idoctum: 'idoctum',
  presentation: 'presentation'
}

/**
 * @type {TKeyValuePairs<TSubject>}
 */
module.exports.SUBJECT = {
  math:                                 'math',
  logopedy:                             'logopedy',
  grammar:                              'grammar',
  'foreign-language':                   'foreign-language',
  literature:                           'literature',
  'environmental-biology':              'environmental-biology',
  biology:                              'biology',
  geography:                            'geography',
  'health-promotion-and-mental-health': 'health-promotion-and-mental-health',
  chemistry:                            'chemistry',
  history:                              'history',
  physics:                              'physics',
  music:                                'music',
  traffic:                              'traffic',
  chess:                                'chess'
}

/**
 * @type {Record<TSubject, [TSubjectColor, TSubjectColor]>}
 */
module.exports.SUBJECT_COLORS = {
  math:                                 [ '#de3831', '#de3831' ],
  logopedy:                             [ '#f49123', '#f49123' ],
  grammar:                              [ '#f15d3f', '#f15d3f' ],
  'foreign-language':                   [ '#4db4d2', '#4db4d2' ],
  literature:                           [ '#e5bd78', '#e5bd78' ],
  'environmental-biology':              [ '#87c543', '#87c543' ],
  biology:                              [ '#518f40', '#518f40' ],
  geography:                            [ '#945e3b', '#945e3b' ],
  'health-promotion-and-mental-health': [ '#f49123', '#f49123' ],
  chemistry:                            [ '#87579e', '#87579e' ],
  history:                              [ '#07867f', '#07867f' ],
  physics:                              [ '#118acb', '#118acb' ],
  music:                                [ '#9f1c36', '#9f1c36' ],
  traffic:                              [ '#f49123', '#f49123' ],
  chess:                                [ '#f49123', '#f49123' ]
}

/** A nyelvi fájlban a staff résznél található nyelvesítések. */
module.exports.STAFF = {
  gfx: {
    debi: 'gortva_debora',
    kozi: 'kozmajer_viktor',
    kata: 'balogh_katalin',
    szabolcs: 'pal_szabolcs',
    gergo: 'gondocs_gergely',
    brigi: 'pentek_brigitta',
    eszter: 'szabo_eszter',
    linda: 'skriba_melinda',
    luca: 'feher_luca',
    mozes: 'szabo_cseke_mozes'
  },
  code: {
    balazs: 'gabor_balazs',
    zsola: 'katai_zsolt',
    igor: 'jerszi_igor',
    ramy: 'kapitany_krisztian',
    akos: 'mate_akos',
    peter: 'csete_peter',
    ati: 'hajtun_attila',
    boti: 'karaszi_botond',
    martin: 'mosonyi_martin',
    mate: 'ocsai_mate_zsigmond'
  },
  content: {
    andika: 'szabolcsine',
    barbi: 'lottos_barbara',
    krisztina: 'reith_krisztina',
    koronkai: 'koronkai_andrea',
    aknai: 'aknai_dora',
    thoroczkay: 'thoroczkay_miklosne',
    nori: 'bollog_nora',
    adri: 'natratics_adrienn'
  },
  manager: {
    judit: 'farkas_judit',
    fru: 'szabolcsi_fruzsina',
    andi: 'bota_andrea'
  },
  narration: {
    berci: 'farkas_bertalan',
    szente: 'szente_arpad_csaba',
    nori: 'bollog_nora'
  }
}

/**
 * @type {TKeyValuePairs<TDevPhase>}
 */
module.exports.DEV_PHASE = {
  done:        'done',        // Fejlesztési szempontból kész app.
  development: 'development', // Fejlesztés alatt álló (még egyszer sem releaselt) app.
  planned:     'planned',     // Tervezett app, de már a skeletonja kész van.
  paused:      'paused'       // Elkezdett, de jelenleg leállított app.
}

/**
 * @type {TKeyValuePairs<TDemoStatus>}
 */
module.exports.DEMO_STATUS = {
  full: 'full',  // A játék teljes verzióban indul.
  demo: 'demo',  // A játék csak demó verzióban indul.
  check: 'check' // A játék felszól a szervernek, hogy milyen verzióban kell indulnia.
}

/**
 * Az adott nyelven is elérhetőnek kell lenniük ezeknek az appoknak.
 * @type {Partial<Record<TLanguage,number[]>>}
 */
module.exports.MULTILANGUAGE_APPS = {
  en: [
    102, 103, 104, 105, 106, 109,
    113, 114, 115, 117, 118,
    120, 121, 125, 126, 127, 128,
    130, 133, 135, 137, 138, 139,
    140, 141, 142, 143, 144, 145, 147, 148,
    151, 152, 153, 154, 155, 157, 158, 159,
    160, 161, 162, 163, 164, 166, 167, 168, 169,
    170, 171, 173, 175, 176, 177, 179,
    180, 181, 182, 183, 184, 185, 186, 187, 188, 189,
    190, 191, 192, 193, 194, 195, 196,
    211, 214, 217,
    221,
    240, 241, 242, 243, 244, 245, 246, 247, 248, 249,
    270, 271, 273, 274, 275,
    280,
    290, 291, 292, 293, 294, 295, 296, 297, 298, 299,
    300,
    320,
    400, 401, 402, 403, 404, 406, 407, 409
  ],
  es: [
    102, 103, 104, 105, 106, 109, 113, 114, 117, 118,
    120, 121, 125, 126, 127, 128, 130, 133, 135, 137,
    139, 151, 152, 153, 154, 155, 157, 158, 159, 160,
    161, 162, 163, 164, 168, 173, 176, 177, 179, 211,
    240, 241, 242, 243, 244, 245, 246, 247, 248, 249,
    270, 271, 273, 274
  ],
  ro: [
    102, 103, 104, 105, 106, 109, 113, 114, 117, 118,
    120, 121, 125, 127, 128, 130, 133, 137, 139, 151,
    152, 153, 154, 155, 157, 158, 159, 160, 161, 162,
    163, 164, 168, 173, 176, 177, 179, 211, 240, 241,
    242, 243, 244, 245, 246, 247, 248, 249, 270, 271
  ],
  sk: [
    180, 181, 182, 183, 184, 185, 186, 187, 188, 189,
    190, 191, 192, 193, 194, 195, 196, 300
  ]
}

/**
 * Azok az appok, amiknek nehéz a nyelvesítése és jelenleg csak egy nyelven elérhetők.
 * @type {readonly number[]}
 */
module.exports.NOT_EASILY_TRANSLATABLE_APPS = [
  115, // Telitalálat: szöveg adatban
  119, // Iskolai előkészítő 2: magyar narráció van hozzá és szöveg adatban
  146, // Építész: szöveg adatban
  149, // Hídjavító: szöveg adatban
  150, // Óraolvasó: nagyon bele kell nyúlni a logikába
  178, // Hangmoly: szöveg adatban
  212, // Betumoly: szöveg adatban
  213, // Molyoló: szöveg adatban
  215, // Igekert: szöveg adatban
  216, // Szósziget: szöveg adatban
  218, // Sajtgolyó: szöveg adatban
  219, // Toldalék buborék: szöveg adatban
  220, // Vándor csiga: szöveg adatban
  222, // Sasles: van angol és német megfelelője (HawkShaw / Muster-detektiv)
  223, // Kutyák és igék: szöveg adatban
  250, // Országjáró 1: szöveg adatban
  251, // Országjáró 2: szöveg adatban
  252, // Európajáró: szöveg adatban
  272, // Mondatlajhár: szöveg adatban
  405, // Számsztori: szöveg adatban

  410, // Sztoridetektív: rengeteg szöveg, kell lektor, egyelőre nincs angolul.

  ...GERMAN_ONLY_APPS,
  ...ENGLISH_ONLY_APPS
]

/** Azok az appok, amikhez nem csinál(t)unk exe-t, mert nincs rá szükség. */
module.exports.APPS_WITHOUT_EXE_FILES = [
  400, 401, 402, 403, 404, 405, 406,
  407, 408, 409, 410, 411, 412, 413, 415
]

/**
 * Azok az appok, ahol tree narráció van.
 * @type {ReadonlyArray<number>}
 */
module.exports.APPS_WITH_TREE_NARRATION = [
  153, 212, 130, 158, 104, 160,
  113, 274, 151, 273, 178, 149,
  161, 164, 117, 147, 213, 168,
  211, 275, 175, 125, 138, 148,
  120, 173, 127,
  109, 104, 157, 128, 133, 135,
  406,
  415
]

/**
 * Azok az appok, ahol user guide narráció van.
 * @type {ReadonlyArray<number>}
 */
module.exports.APPS_WITH_USER_GUIDE_NARRATION = [
  153, 212, 130, 158, 104, 160,
  274, 151, 273, 178, 149, 161,
  164, 119, 117, 147, 213, 168,
  275, 175, 125, 138, 148, 120,
  127,
  109, 104, 157, 406,
  415
]

/**
 * Azok az appok, ahol game narráció van.
 * @type {ReadonlyArray<number>}
 */
module.exports.APPS_WITH_GAME_NARRATION = [
  153, 212, 130, 158,
  113, 178, 149, 290,
  119, 159, 213, 114,
  175, 138, 173, 412,
  415
]

/**
 * @type {TReadonlyRecord<'start' | 'end', string>}
 */
module.exports.GENERATED_NARRATION_MAGIG_COMMENT = {
  start: '// @GOKU: makeAppNarration generated narration starts',
  end:   '// @GOKU: makeAppNarration generated narration ends'
}
